import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Building2, Landmark, Save, Lock, KeyRound, UserCog, HardDrive, LinkIcon, Unplug } from "lucide-react";
import { toast } from "sonner";
import CatalogManager from "./CatalogManager";
import ExpenseCategoryManager from "./ExpenseCategoryManager";

const empty = {
  company_name: "", company_address: "", company_phone: "", company_email: "",
  bank_name: "", bank_account_no: "", bank_account_holder: "",
  bank_name_2: "", bank_account_no_2: "", bank_account_holder_2: "",
  doku_secret_key: "", doku_public_key: "", doku_enabled: false, payment_link_template: "",
  wa_gateway_enabled: false, waha_url: "", waha_api_key: "", waha_webhook_url: "http://172.17.0.1:8000/api/wa/webhook",
  wa_humanize_enabled: true, wa_daily_limit: 30, wa_delay_min: 45, wa_delay_max: 120,
  wa_send_start_hour: 7, wa_send_end_hour: 21, wa_ai_paraphrase_enabled: true, gemini_api_key: "",
  wa_auto_lunas_report: true, wa_auto_selesai_report: true, wa_auto_gagal_report: true, wa_report_sumatra_phone: "082363358269", wa_auto_absen_reminder: true, wa_auto_bulk_customer: true, wa_auto_bulk_technician: true,
  bbm_price: 0, motor_consumption: 0,
  petty_cash_min_balance: 0,
  arrival_radius_m: 300,
  email_notify_enabled: false, email_notify_time: "18:00", email_recipient: "",
  smtp_host: "", smtp_port: 465, smtp_user: "", smtp_password: "", smtp_from: "",
  invoice_footer: "", logo_base64: "", qris_payload: "", qris_image_base64: "",
};

const Settings = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(true);
  const [lockPwd, setLockPwd] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  // login credentials change
  const [credForm, setCredForm] = useState({ current_password: "", new_email: "", new_password: "" });
  const [credSaving, setCredSaving] = useState(false);

  // settings password change
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  // drive connection status
  const [drive, setDrive] = useState({ connected: false, email: "", root_folder_id: "" });
  const [driveConnecting, setDriveConnecting] = useState(false);

  // WAHA status & QR
  const [wahaStatus, setWahaStatus] = useState(null);
  const [wahaQr, setWahaQr] = useState(null);
  const [wahaBusy, setWahaBusy] = useState(false);
  const wahaPollRef = React.useRef(null);

  const stopWahaPoll = () => {
    if (wahaPollRef.current) { clearInterval(wahaPollRef.current); wahaPollRef.current = null; }
  };
  useEffect(() => () => stopWahaPoll(), []);

  const checkWahaStatus = async () => {
    setWahaBusy(true);
    try {
      const { data } = await api.get("/wa/status");
      setWahaStatus(data);
      if (data.status === "WORKING") { setWahaQr(null); stopWahaPoll(); toast.success("WhatsApp terhubung"); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal cek status WAHA");
    } finally { setWahaBusy(false); }
  };

  const startWahaPoll = () => {
    stopWahaPoll();
    let ticks = 0;
    wahaPollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > 12) { stopWahaPoll(); return; }  // stop setelah ±2 menit
      try {
        const { data: st } = await api.get("/wa/status");
        setWahaStatus(st);
        if (st.status === "WORKING") {
          setWahaQr(null); stopWahaPoll();
          toast.success("WhatsApp terhubung!");
          return;
        }
        const { data: qr } = await api.get("/wa/qr");
        setWahaQr(qr);  // QR di-refresh otomatis supaya tidak kedaluwarsa
      } catch (_) { /* biarkan poll berikutnya mencoba lagi */ }
    }, 10000);
  };

  const startWahaSession = async () => {
    setWahaBusy(true);
    setWahaQr(null);
    try {
      const { data: s } = await api.post("/wa/session/start");
      if (s.status === "WORKING") {
        setWahaStatus({ configured: true, status: "WORKING" });
        stopWahaPoll();
        toast.success("Session sudah terhubung");
        return;
      }
      setWahaStatus({ configured: true, status: s.status || "STARTING" });
      const { data: qr } = await api.get("/wa/qr");
      setWahaQr(qr);
      startWahaPoll();
      toast.success("Scan QR sekarang — QR diperbarui otomatis setiap 10 detik");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memulai session WAHA", { duration: 8000 });
    } finally { setWahaBusy(false); }
  };

  const resetWahaSession = async () => {
    if (!window.confirm("Reset session akan LOGOUT dari WhatsApp yang tertaut dan meminta scan QR baru. Lanjutkan?")) return;
    setWahaBusy(true);
    setWahaQr(null);
    try {
      toast.info("Mereset session… tunggu sebentar (±30 detik)");
      const { data: s } = await api.post("/wa/session/reset");
      setWahaStatus({ configured: true, status: s.status || "STARTING" });
      const { data: qr } = await api.get("/wa/qr");
      setWahaQr(qr);
      startWahaPoll();
      toast.success("Session di-reset — scan QR baru sekarang");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal reset session WAHA", { duration: 8000 });
    } finally { setWahaBusy(false); }
  };

  const fetchDriveStatus = useCallback(async () => {
    try {
      const r = await api.get("/drive/oauth/status");
      setDrive(r.data || { connected: false });
    } catch { /* ignore */ }
  }, []);

  const connectDrive = async () => {
    setDriveConnecting(true);
    try {
      const r = await api.get("/drive/oauth/start");
      const w = window.open(r.data.auth_url, "drive_oauth", "width=560,height=720");
      const onMsg = async (ev) => {
        if (ev?.data?.type === "drive_oauth") {
          window.removeEventListener("message", onMsg);
          await fetchDriveStatus();
          setDriveConnecting(false);
          if (ev.data.ok) toast.success("Google Drive terhubung");
          else toast.error("Gagal menghubungkan Drive");
        }
      };
      window.addEventListener("message", onMsg);
      // Fallback: poll if popup closed without postMessage
      const poll = setInterval(async () => {
        if (w?.closed) {
          clearInterval(poll);
          window.removeEventListener("message", onMsg);
          await fetchDriveStatus();
          setDriveConnecting(false);
        }
      }, 800);
    } catch (e) {
      setDriveConnecting(false);
      toast.error(e?.response?.data?.detail || "Gagal memulai OAuth");
    }
  };

  const disconnectDrive = async () => {
    if (!window.confirm("Putuskan koneksi Google Drive? File yang sudah di-upload tetap ada di Drive Anda.")) return;
    try {
      await api.post("/drive/oauth/disconnect");
      await fetchDriveStatus();
      toast.success("Drive diputus");
    } catch {
      toast.error("Gagal memutus");
    }
  };

  useEffect(() => {
    // Password gate selalu aktif setiap kali halaman Pengaturan dibuka (keamanan).
    // Bersihkan cache lama dari versi sebelumnya (kalau ada).
    try { localStorage.removeItem("om_settings_unlocked_at"); } catch (_) { /* noop */ }
    try { sessionStorage.removeItem("om_settings_unlocked_at"); } catch (_) { /* noop */ }
    setLoading(false);
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings");
      setForm({ ...empty, ...r.data });
    } finally {
      setLoading(false);
    }
  };

  const submitUnlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    try {
      await api.post("/settings/unlock", { password: lockPwd });
      // Tidak ada caching — unlock hanya berlaku untuk sesi halaman ini.
      setLocked(false);
      setLockPwd("");
      toast.success("Akses pengaturan terbuka");
      fetchSettings();
      fetchDriveStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password salah");
    } finally {
      setUnlocking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings", form);
      setForm({ ...empty, ...data });
      toast.success("Pengaturan disimpan. Invoice akan otomatis pakai data baru.");
    } catch {
      toast.error("Gagal menyimpan pengaturan");
    } finally {
      setSaving(false);
    }
  };

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300 * 1024) { toast.error("Logo maks 300KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    if (!credForm.current_password) { toast.error("Isi password saat ini"); return; }
    if (!credForm.new_email && !credForm.new_password) { toast.error("Isi email atau password baru"); return; }
    setCredSaving(true);
    try {
      await api.put("/auth/credentials", credForm);
      toast.success("Akun diperbarui. Anda mungkin perlu login ulang dengan kredensial baru.");
      setCredForm({ current_password: "", new_email: "", new_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui akun");
    } finally {
      setCredSaving(false);
    }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    if (!pwdForm.current_password || !pwdForm.new_password) { toast.error("Isi semua field password"); return; }
    setPwdSaving(true);
    try {
      await api.put("/settings/password", pwdForm);
      toast.success("Password pengaturan diperbarui");
      setPwdForm({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengubah password");
    } finally {
      setPwdSaving(false);
    }
  };

  // ----- Lock screen -----
  if (locked) {
    return (
      <Layout>
        <div className="max-w-md mx-auto mt-12 bg-white border border-[#E4E4E7]" data-testid="settings-lock-screen">
          <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
            <Lock size={14} />
            <div className="label-cap">Akses Terkunci</div>
          </div>
          <form onSubmit={submitUnlock} className="p-6 space-y-4">
            <div>
              <h2 className="font-heading text-2xl font-black mb-1">Password Pengaturan</h2>
              <p className="text-sm text-[#52525B]">Halaman ini diproteksi. Masukkan password pengaturan untuk lanjut.</p>
            </div>
            <input
              type="password"
              autoFocus
              required
              value={lockPwd}
              onChange={(e) => setLockPwd(e.target.value)}
              placeholder="Password pengaturan"
              data-testid="settings-unlock-input"
              className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white font-mono"
            />
            <button type="submit" disabled={unlocking} data-testid="settings-unlock-button" className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              <Lock size={14} /> {unlocking ? "Memverifikasi…" : "Buka Pengaturan"}
            </button>
          </form>
        </div>
      </Layout>
    );
  }

  if (loading) return <Layout><div className="label-cap">Memuat…</div></Layout>;

  return (
    <Layout>
      <div className="mb-8">
        <div className="label-cap mb-2">Konfigurasi</div>
        <h1 className="font-heading text-4xl font-black tracking-tight">Pengaturan</h1>
        <p className="text-sm text-[#52525B] mt-2 max-w-xl">Data perusahaan & rekening di sini akan otomatis tampil di setiap PDF invoice dan pesan WhatsApp invoice yang Anda kirim ke pelanggan.</p>
      </div>

      <form onSubmit={submit} data-testid="settings-form" className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        {/* Company */}
        <div className="bg-white border border-[#E4E4E7]">
          <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
            <Building2 size={14} />
            <div className="label-cap">Identitas Perusahaan</div>
          </div>
          <div className="p-6 space-y-4">
            <Field label="Nama Perusahaan">
              <input data-testid="settings-company-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="CV Maju Jaya Service" className={inputCls} />
            </Field>
            <Field label="Alamat">
              <textarea data-testid="settings-company-address" value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} rows={2} placeholder="Jl. Sudirman No. 123, Jakarta" className={inputCls} />
            </Field>
            <Field label="Telepon">
              <input data-testid="settings-company-phone" value={form.company_phone} onChange={(e) => setForm({ ...form, company_phone: e.target.value })} placeholder="0812-3456-7890" className={inputCls + " font-mono"} />
            </Field>
            <Field label="Email">
              <input type="email" data-testid="settings-company-email" value={form.company_email} onChange={(e) => setForm({ ...form, company_email: e.target.value })} placeholder="info@majujaya.id" className={inputCls + " font-mono"} />
            </Field>
            <Field label="Logo Perusahaan (tampil di PDF Invoice, maks 300KB)">
              <div className="flex items-center gap-3">
                {form.logo_base64 ? (
                  <img src={form.logo_base64} alt="logo" className="h-16 w-16 border border-[#E4E4E7] object-contain bg-white" data-testid="settings-logo-preview" />
                ) : (
                  <div className="h-16 w-16 border border-dashed border-[#E4E4E7] flex items-center justify-center text-xs text-[#52525B]">Tidak ada</div>
                )}
                <input type="file" accept="image/*" onChange={onLogoChange} data-testid="settings-logo-input" className="text-xs" />
                {form.logo_base64 && <button type="button" onClick={() => setForm({ ...form, logo_base64: "" })} className="text-xs text-[#DC2626] underline">Hapus</button>}
              </div>
            </Field>
          </div>
        </div>

        {/* Bank */}
        <div className="bg-white border border-[#E4E4E7]">
          <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
            <Landmark size={14} />
            <div className="label-cap">Rekening Pembayaran (Tampil di Invoice)</div>
          </div>
          <div className="p-6 space-y-4">
            <Field label="Nama Bank">
              <input data-testid="settings-bank-name" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="Bank BCA / BRI / Mandiri" className={inputCls} />
            </Field>
            <Field label="Nomor Rekening">
              <input data-testid="settings-bank-account-no" value={form.bank_account_no} onChange={(e) => setForm({ ...form, bank_account_no: e.target.value })} placeholder="1234567890" className={inputCls + " font-mono"} />
            </Field>
            <Field label="Atas Nama">
              <input data-testid="settings-bank-account-holder" value={form.bank_account_holder} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })} placeholder="PT Maju Jaya / Budi Santoso" className={inputCls} />
            </Field>
            <div className="border-t border-[#E4E4E7] pt-4 mt-2">
              <div className="label-cap mb-3">Rekening Kedua (Opsional)</div>
              <Field label="Nama Bank 2">
                <input data-testid="settings-bank-name-2" value={form.bank_name_2} onChange={(e) => setForm({ ...form, bank_name_2: e.target.value })} placeholder="Bank Mandiri" className={inputCls} />
              </Field>
              <Field label="Nomor Rekening 2">
                <input data-testid="settings-bank-account-no-2" value={form.bank_account_no_2} onChange={(e) => setForm({ ...form, bank_account_no_2: e.target.value })} placeholder="0987654321" className={inputCls + " font-mono mt-3"} />
              </Field>
              <Field label="Atas Nama 2">
                <input data-testid="settings-bank-account-holder-2" value={form.bank_account_holder_2} onChange={(e) => setForm({ ...form, bank_account_holder_2: e.target.value })} placeholder="PT Maju Jaya" className={inputCls + " mt-3"} />
              </Field>
            </div>
            <div className="border-t border-[#E4E4E7] pt-4 mt-2">
              <div className="label-cap mb-3">DOKU Payment Gateway (Opsional)</div>
              <label className="flex items-start gap-2 cursor-pointer mb-3" data-testid="settings-doku-enabled-wrapper">
                <input type="checkbox" data-testid="settings-doku-enabled" checked={!!form.doku_enabled} onChange={(e) => setForm({ ...form, doku_enabled: e.target.checked })} className="mt-0.5 w-4 h-4 accent-[#002FA7]" />
                <div>
                  <div className="text-sm font-bold">Aktifkan link DOKU di pesan WA tagihan</div>
                  <div className="text-[10px] text-[#52525B] mt-0.5">Jika dicentang, link pembayaran DOKU akan disisipkan ke pesan invoice WhatsApp ke pelanggan.</div>
                </div>
              </label>
              <Field label="DOKU Secret Key">
                <input type="password" data-testid="settings-doku-secret" value={form.doku_secret_key} onChange={(e) => setForm({ ...form, doku_secret_key: e.target.value })} placeholder="SK-xxxxxx" className={inputCls + " font-mono text-xs"} />
              </Field>
              <Field label="DOKU Public Key (PEM)">
                <textarea data-testid="settings-doku-public" value={form.doku_public_key} onChange={(e) => setForm({ ...form, doku_public_key: e.target.value })} rows={4} placeholder="-----BEGIN PUBLIC KEY-----..." className={inputCls + " font-mono text-[10px] mt-3"} />
              </Field>
              <Field label="Link Pembayaran (Template)">
                <input data-testid="settings-payment-link" value={form.payment_link_template} onChange={(e) => setForm({ ...form, payment_link_template: e.target.value })} placeholder="https://pay.doku.com/checkout/{order_no}" className={inputCls + " font-mono text-xs mt-3"} />
                <p className="text-[10px] text-[#52525B] mt-1">Akan muncul di pesan WA invoice. Gunakan placeholder <code>{"{order_no}"}</code> dan <code>{"{total}"}</code> untuk substitusi otomatis.</p>
              </Field>
            </div>

            <div className="border-t border-[#E4E4E7] pt-4 mt-2">
              <div className="label-cap mb-3">WhatsApp Gateway — WAHA (Self-Hosted)</div>
              <label className="flex items-start gap-2 cursor-pointer mb-3" data-testid="settings-wa-gateway-enabled-wrapper">
                <input type="checkbox" data-testid="settings-wa-gateway-enabled" checked={!!form.wa_gateway_enabled} onChange={(e) => setForm({ ...form, wa_gateway_enabled: e.target.checked })} className="mt-0.5 w-4 h-4 accent-[#002FA7]" />
                <div>
                  <div className="text-sm font-bold">Kirim WA otomatis via WAHA</div>
                  <div className="text-[10px] text-[#52525B] mt-0.5">Pesan teks bebas tanpa template Meta. Jika tidak dicentang, tombol WA membuka WhatsApp lokal (wa.me) seperti biasa.</div>
                </div>
              </label>
              {form.wa_gateway_enabled && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="URL WAHA">
                      <input data-testid="settings-waha-url" value={form.waha_url} onChange={(e) => setForm({ ...form, waha_url: e.target.value })} placeholder="http://127.0.0.1:3011" className={inputCls + " font-mono text-xs"} />
                    </Field>
                    <Field label="API Key WAHA">
                      <input type="password" data-testid="settings-waha-key" value={form.waha_api_key} onChange={(e) => setForm({ ...form, waha_api_key: e.target.value })} placeholder="kosongkan bila tidak ingin mengubah" className={inputCls + " font-mono text-xs"} />
                    </Field>
                  </div>
                  <Field label="Webhook URL (Inbox 2 Arah)">
                    <input data-testid="settings-waha-webhook" value={form.waha_webhook_url} onChange={(e) => setForm({ ...form, waha_webhook_url: e.target.value })} placeholder="http://172.17.0.1:8000/api/wa/webhook" className={inputCls + " font-mono text-xs"} />
                    <p className="text-[10px] text-[#52525B] mt-1">Alamat aplikasi ini dilihat dari container WAHA. Terpasang otomatis setiap session dibuat/di-reset agar percakapan masuk ke Inbox.</p>
                  </Field>
                  <div className="border border-[#E4E4E7] bg-[#FAFAFA] p-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Status Session:</span>
                      <span data-testid="waha-status" className={`text-xs font-mono font-bold ${wahaStatus?.status === "WORKING" ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
                        {wahaStatus ? (wahaStatus.status === "WORKING" ? `TERHUBUNG ${wahaStatus.me?.pushName ? `(${wahaStatus.me.pushName})` : ""}` : wahaStatus.status) : "—"}
                      </span>
                      <button type="button" data-testid="waha-check-status" onClick={checkWahaStatus} disabled={wahaBusy} className="px-2 py-1 border border-[#E4E4E7] bg-white text-[10px] font-bold uppercase hover:bg-[#F4F4F5] disabled:opacity-50">Cek Status</button>
                      <button type="button" data-testid="waha-start-session" onClick={startWahaSession} disabled={wahaBusy} className="px-2 py-1 bg-[#002FA7] text-white text-[10px] font-bold uppercase hover:opacity-90 disabled:opacity-50">Mulai Session + QR</button>
                      <button type="button" data-testid="waha-reset-session" onClick={resetWahaSession} disabled={wahaBusy} className="px-2 py-1 bg-[#DC2626] text-white text-[10px] font-bold uppercase hover:opacity-90 disabled:opacity-50">Reset Session (QR Baru)</button>
                    </div>
                    {wahaQr && (
                      <div className="space-y-1">
                        <img data-testid="waha-qr-image" src={`data:${wahaQr.mimetype};base64,${wahaQr.data}`} alt="QR WhatsApp" className="w-52 h-52 border border-[#E4E4E7] bg-white" />
                        <div className="text-[10px] text-[#52525B]">Scan dengan WhatsApp di HP nomor bisnis: <b>Setelan → Perangkat Tertaut → Tautkan Perangkat</b>. QR diperbarui otomatis setiap 10 detik — biarkan halaman ini terbuka sampai status TERHUBUNG.</div>
                      </div>
                    )}
                    {wahaStatus?.error && <div className="text-[10px] text-[#DC2626]">{wahaStatus.error}</div>}
                  </div>

                  <div className="border border-[#E4E4E7] bg-[#FAFAFA] p-3 space-y-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" data-testid="settings-wa-humanize" checked={!!form.wa_humanize_enabled} onChange={(e) => setForm({ ...form, wa_humanize_enabled: e.target.checked })} className="mt-0.5 w-4 h-4 accent-[#002FA7]" />
                      <div>
                        <div className="text-sm font-bold">Humanisasi Pengiriman (Anti-Restriksi Meta)</div>
                        <div className="text-[10px] text-[#52525B] mt-0.5">Semua pesan masuk antrian lalu dikirim satu per satu dengan jeda acak + simulasi mengetik, dibatasi kuota harian & jam kirim. Sangat disarankan tetap aktif.</div>
                      </div>
                    </label>
                    {form.wa_humanize_enabled && (
                      <>
                        <div className="grid grid-cols-3 gap-3">
                          <Field label="Max Pesan / Hari">
                            <input type="number" min="1" data-testid="settings-wa-daily-limit" value={form.wa_daily_limit} onChange={(e) => setForm({ ...form, wa_daily_limit: Number(e.target.value || 0) })} className={inputCls + " font-mono"} />
                          </Field>
                          <Field label="Jeda Min (detik)">
                            <input type="number" min="10" data-testid="settings-wa-delay-min" value={form.wa_delay_min} onChange={(e) => setForm({ ...form, wa_delay_min: Number(e.target.value || 0) })} className={inputCls + " font-mono"} />
                          </Field>
                          <Field label="Jeda Max (detik)">
                            <input type="number" min="10" data-testid="settings-wa-delay-max" value={form.wa_delay_max} onChange={(e) => setForm({ ...form, wa_delay_max: Number(e.target.value || 0) })} className={inputCls + " font-mono"} />
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Field label="Jam Mulai Kirim (WIB)">
                            <input type="number" min="0" max="23" data-testid="settings-wa-start-hour" value={form.wa_send_start_hour} onChange={(e) => setForm({ ...form, wa_send_start_hour: Number(e.target.value || 0) })} className={inputCls + " font-mono"} />
                          </Field>
                          <Field label="Jam Selesai Kirim (WIB)">
                            <input type="number" min="1" max="24" data-testid="settings-wa-end-hour" value={form.wa_send_end_hour} onChange={(e) => setForm({ ...form, wa_send_end_hour: Number(e.target.value || 0) })} className={inputCls + " font-mono"} />
                          </Field>
                        </div>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input type="checkbox" data-testid="settings-wa-ai-paraphrase" checked={!!form.wa_ai_paraphrase_enabled} onChange={(e) => setForm({ ...form, wa_ai_paraphrase_enabled: e.target.checked })} className="mt-0.5 w-4 h-4 accent-[#002FA7]" />
                          <div>
                            <div className="text-sm font-bold">AI Parafrase Pesan (Gemini)</div>
                            <div className="text-[10px] text-[#52525B] mt-0.5">Setiap pesan ditulis ulang AI dengan gaya sedikit berbeda (angka, harga & link dijamin tidak berubah) agar tidak terdeteksi sebagai pesan massal identik.</div>
                          </div>
                        </label>
                        {form.wa_ai_paraphrase_enabled && (
                          <Field label="Gemini API Key">
                            <input type="password" data-testid="settings-gemini-key" value={form.gemini_api_key} onChange={(e) => setForm({ ...form, gemini_api_key: e.target.value })} placeholder="kosongkan bila tidak ingin mengubah" className={inputCls + " font-mono text-xs"} />
                            <p className="text-[10px] text-[#52525B] mt-1">Dapatkan gratis di aistudio.google.com → Get API Key. Bila kosong, pesan dikirim tanpa parafrase.</p>
                          </Field>
                        )}
                      </>
                    )}
                  </div>

                  <div className="border border-[#E4E4E7] bg-[#FAFAFA] p-3 space-y-2">
                    <div className="text-sm font-bold">Pesan Otomatis — Switch On/Off</div>
                    <div className="text-[10px] text-[#52525B]">Pilih jenis pesan yang boleh dikirim otomatis oleh sistem. Yang dimatikan tidak akan dikirim sama sekali.</div>
                    {[
                      { key: "wa_auto_selesai_report", label: "Laporan Order Selesai", desc: "Terkirim otomatis ke nomor owner saat status pekerjaan order diubah menjadi Selesai." },
                      { key: "wa_auto_gagal_report", label: "Laporan Order Gagal", desc: "Terkirim otomatis ke nomor owner saat status pekerjaan order diubah menjadi Gagal (catatan order ikut dikirim)." },
                      { key: "wa_auto_lunas_report", label: "Laporan Order Lunas", desc: "Terkirim otomatis ke nomor owner saat pembayaran order diubah menjadi Lunas. Bila Selesai + Lunas disimpan bersamaan → digabung 1 pesan." },
                      { key: "wa_auto_absen_reminder", label: "Pengingat Absen Pagi Karyawan", desc: "Terkirim tiap pagi (08:15 WIB) ke karyawan yang dicentang & belum absen." },
                      { key: "wa_auto_bulk_customer", label: "Reminder Jadwal ke Pelanggan (Bulk)", desc: "Pengingat jadwal pekerjaan H-1/hari-H ke pelanggan via tombol Kirim Bulk." },
                      { key: "wa_auto_bulk_technician", label: "Reminder Penugasan ke Teknisi (Bulk)", desc: "Pengingat penugasan + alamat + shareloc ke teknisi via tombol Kirim Bulk." },
                    ].map((sw) => (
                      <label key={sw.key} className="flex items-start justify-between gap-3 cursor-pointer border border-[#E4E4E7] bg-white px-3 py-2" data-testid={`settings-${sw.key.replaceAll("_", "-")}-wrapper`}>
                        <div>
                          <div className="text-xs font-bold">{sw.label}</div>
                          <div className="text-[10px] text-[#52525B] mt-0.5">{sw.desc}</div>
                        </div>
                        <div className="relative inline-flex items-center shrink-0 mt-1">
                          <input type="checkbox" data-testid={`settings-${sw.key.replaceAll("_", "-")}`} checked={!!form[sw.key]} onChange={(e) => setForm({ ...form, [sw.key]: e.target.checked })} className="sr-only peer" />
                          <div className="w-9 h-5 bg-[#D4D4D8] peer-checked:bg-[#059669] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-4" />
                        </div>
                      </label>
                    ))}
                    <div className="border border-[#E4E4E7] bg-white px-3 py-2" data-testid="settings-wa-report-sumatra-wrapper">
                      <div className="text-xs font-bold">No WA Tambahan — Wilayah Riau, Sumut & Aceh</div>
                      <div className="text-[10px] text-[#52525B] mt-0.5">Laporan Selesai / Gagal / Lunas untuk order pelanggan di Provinsi Riau, Sumatera Utara & Aceh juga dikirim ke nomor ini (nomor owner tetap terima). Wilayah dibaca dari kota pelanggan; bila kota kosong → alamat, lalu base teknisi (Medan). Kosongkan untuk menonaktifkan. Kalimat pesan disusun acak tiap kirim agar tidak terlihat seperti template.</div>
                      <input type="tel" data-testid="settings-wa-report-sumatra-phone" value={form.wa_report_sumatra_phone || ""} onChange={(e) => setForm({ ...form, wa_report_sumatra_phone: e.target.value })} placeholder="08xxxxxxxxxx" className={inputCls + " mt-2 font-mono text-xs"} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-[#E4E4E7] pt-4 mt-2">
              <div className="label-cap mb-3">Jarak Tempuh & BBM (Uang Bensin Otomatis)</div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Harga BBM per Liter (Rp)">
                  <input type="number" min="0" data-testid="settings-bbm-price" value={form.bbm_price} onChange={(e) => setForm({ ...form, bbm_price: Number(e.target.value || 0) })} placeholder="10000" className={inputCls + " font-mono"} />
                </Field>
                <Field label="Konsumsi Motor (km/liter)">
                  <input type="number" min="0" step="0.1" data-testid="settings-motor-consumption" value={form.motor_consumption} onChange={(e) => setForm({ ...form, motor_consumption: Number(e.target.value || 0) })} placeholder="40" className={inputCls + " font-mono"} />
                </Field>
              </div>
              <p className="text-[10px] text-[#52525B] mt-1">Dipakai menu Jarak Tempuh: Liter = (Jarak PP × 1.1) ÷ konsumsi motor, Uang Bensin = Liter × harga BBM. Kendaraan Mobil tidak dihitung.</p>
            </div>

            <div className="border-t border-[#E4E4E7] pt-4 mt-2">
              <div className="label-cap mb-3">Petty Cash (Kas Tunai Admin)</div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Batas Bawah Saldo (Rp)">
                  <input type="number" min="0" data-testid="settings-petty-min" value={form.petty_cash_min_balance} onChange={(e) => setForm({ ...form, petty_cash_min_balance: Number(e.target.value || 0) })} placeholder="500000" className={inputCls + " font-mono"} />
                </Field>
              </div>
              <p className="text-[10px] text-[#52525B] mt-1">Bila saldo petty cash sama dengan atau di bawah batas bawah, info otomatis terkirim via WhatsApp ke nomor yang sama dengan Laporan Order Selesai & Lunas. Isi 0 untuk menonaktifkan.</p>
            </div>

            <div className="border-t border-[#E4E4E7] pt-4 mt-2">
              <div className="label-cap mb-3">Notifikasi Teknisi Tiba di Lokasi</div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Radius Lokasi Customer (meter)">
                  <input type="number" min="50" max="5000" step="10" data-testid="settings-arrival-radius" value={form.arrival_radius_m ?? 300} onChange={(e) => setForm({ ...form, arrival_radius_m: Number(e.target.value || 0) })} placeholder="300" className={inputCls + " font-mono"} />
                </Field>
              </div>
              <p className="text-[10px] text-[#52525B] mt-1">Saat aplikasi absensi teknisi terbuka di hari jadwalnya dan posisinya masuk radius ini dari titik share loc customer, muncul notifikasi "Teknisi Tiba di Lokasi" di app ini. Standar 300 m (min 50, maks 5000). Customer tanpa share loc tidak bisa dideteksi.</p>
            </div>

            <div className="border-t border-[#E4E4E7] pt-4 mt-2">
              <div className="label-cap mb-3">Notifikasi Email Laporan Harian</div>
              <label className="flex items-start gap-2 cursor-pointer mb-3">
                <input type="checkbox" data-testid="settings-email-notify-enabled" checked={!!form.email_notify_enabled} onChange={(e) => setForm({ ...form, email_notify_enabled: e.target.checked })} className="mt-0.5 w-4 h-4 accent-[#002FA7]" />
                <div>
                  <div className="text-sm font-bold">Aktifkan pengiriman laporan otomatis</div>
                  <div className="text-[10px] text-[#52525B] mt-0.5">Setiap hari pada jam yang ditentukan, sistem akan kirim email berisi laporan order hari itu.</div>
                </div>
              </label>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Jam Kirim (WIB)">
                  <input type="time" data-testid="settings-email-notify-time" value={form.email_notify_time} onChange={(e) => setForm({ ...form, email_notify_time: e.target.value })} className={inputCls + " font-mono"} />
                </Field>
                <Field label="Email Tujuan">
                  <input type="email" data-testid="settings-email-recipient" value={form.email_recipient} onChange={(e) => setForm({ ...form, email_recipient: e.target.value })} placeholder="atasan@perusahaan.com" className={inputCls + " font-mono"} />
                </Field>
                <Field label="SMTP Host">
                  <input data-testid="settings-smtp-host" value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} placeholder="deteksipipabocor.com" className={inputCls + " font-mono"} />
                </Field>
                <Field label="SMTP Port">
                  <input type="number" data-testid="settings-smtp-port" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: Number(e.target.value || 465) })} placeholder="465" className={inputCls + " font-mono"} />
                </Field>
                <Field label="SMTP Username">
                  <input data-testid="settings-smtp-user" value={form.smtp_user} onChange={(e) => setForm({ ...form, smtp_user: e.target.value })} placeholder="report@deteksipipabocor.com" className={inputCls + " font-mono"} />
                </Field>
                <Field label="SMTP Password">
                  <input type="password" data-testid="settings-smtp-password" value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} placeholder="kosongkan bila tidak ingin mengubah" className={inputCls + " font-mono"} />
                </Field>
                <Field label="Alamat 'From' (opsional)" colSpan>
                  <input data-testid="settings-smtp-from" value={form.smtp_from} onChange={(e) => setForm({ ...form, smtp_from: e.target.value })} placeholder="Order.OS <report@deteksipipabocor.com> — kosongkan pakai SMTP Username" className={inputCls + " font-mono"} />
                </Field>
              </div>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const r = await api.post("/settings/test-email");
                    toast.success(`Test email terkirim ke ${r.data.sent_to} (${r.data.orders_count} order)`);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Gagal kirim test email");
                  }
                }}
                data-testid="settings-test-email-button"
                className="mt-3 px-4 py-2 bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
              >
                📧 Kirim Test Email Sekarang
              </button>
              <p className="text-[10px] text-[#52525B] mt-2">Simpan pengaturan dulu sebelum test. Port 465 = SMTPS/SSL, Port 587 = STARTTLS.</p>
            </div>
            <Field label="Catatan Bawah Invoice">
              <textarea data-testid="settings-invoice-footer" value={form.invoice_footer} onChange={(e) => setForm({ ...form, invoice_footer: e.target.value })} rows={2} placeholder="Terima kasih atas kepercayaan Anda." className={inputCls} />
            </Field>
            <Field label="QRIS Statis — Upload Gambar (jpg/png, maks 1MB)">
              <div className="flex items-start gap-3">
                {form.qris_image_base64 ? (
                  <img src={form.qris_image_base64} alt="QRIS" className="h-32 w-32 object-contain bg-white border border-[#E4E4E7]" data-testid="settings-qris-preview" />
                ) : (
                  <div className="h-32 w-32 border border-dashed border-[#E4E4E7] flex items-center justify-center text-xs text-[#52525B] text-center px-2">Belum ada<br />gambar QRIS</div>
                )}
                <div className="flex-1 space-y-2">
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    data-testid="settings-qris-image-input"
                    className="text-xs block"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 1024 * 1024) { toast.error("Gambar QRIS maks 1MB"); return; }
                      const r = new FileReader();
                      r.onload = () => setForm((f) => ({ ...f, qris_image_base64: r.result }));
                      r.readAsDataURL(file);
                    }}
                  />
                  {form.qris_image_base64 && (
                    <button type="button" onClick={() => setForm({ ...form, qris_image_base64: "" })} className="text-xs text-[#DC2626] underline" data-testid="settings-qris-clear">Hapus gambar</button>
                  )}
                  <p className="text-[10px] text-[#52525B]">Upload screenshot QRIS statis Anda (sudah include nama merchant, NMID, dll). Gambar akan ditempel apa adanya ke PDF Invoice.</p>
                </div>
              </div>
            </Field>
          </div>
        </div>

        <div className="lg:col-span-2 flex justify-end">
          <button type="submit" disabled={saving} data-testid="settings-submit-button" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 flex items-center gap-2 disabled:opacity-60">
            <Save size={14} /> {saving ? "Menyimpan…" : "Simpan Pengaturan"}
          </button>
        </div>
      </form>

      {/* Security: Akun Login + Password Pengaturan */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mt-8">
        <form onSubmit={submitCreds} className="bg-white border border-[#E4E4E7]" data-testid="creds-form">
          <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
            <UserCog size={14} />
            <div className="label-cap">Akun Login Web App</div>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-xs text-[#52525B]">Ubah email atau password yang Anda pakai untuk login ke web app ini.</p>
            <Field label="Password Saat Ini" required>
              <input type="password" required value={credForm.current_password} onChange={(e) => setCredForm({ ...credForm, current_password: e.target.value })} className={inputCls + " font-mono"} data-testid="creds-current-password" />
            </Field>
            <Field label="Email Baru (opsional)">
              <input type="email" value={credForm.new_email} onChange={(e) => setCredForm({ ...credForm, new_email: e.target.value })} placeholder="kosongkan jika tidak ingin diubah" className={inputCls + " font-mono"} data-testid="creds-new-email" />
            </Field>
            <Field label="Password Baru (opsional, min 6 karakter)">
              <input type="password" value={credForm.new_password} onChange={(e) => setCredForm({ ...credForm, new_password: e.target.value })} placeholder="kosongkan jika tidak ingin diubah" className={inputCls + " font-mono"} data-testid="creds-new-password" />
            </Field>
            <button type="submit" disabled={credSaving} data-testid="creds-submit-button" className="w-full bg-[#111113] hover:bg-black text-white text-xs font-bold uppercase tracking-wider px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              <UserCog size={14} /> {credSaving ? "Menyimpan…" : "Simpan Akun Login"}
            </button>
          </div>
        </form>

        <form onSubmit={submitPwd} className="bg-white border border-[#E4E4E7]" data-testid="settings-pwd-form">
          <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
            <KeyRound size={14} />
            <div className="label-cap">Password Pengaturan</div>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-xs text-[#52525B]">Password ini hanya untuk membuka halaman Pengaturan (berbeda dari password login).</p>
            <Field label="Password Pengaturan Saat Ini" required>
              <input type="password" required value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} className={inputCls + " font-mono"} data-testid="settings-pwd-current" />
            </Field>
            <Field label="Password Baru (min 6 karakter)" required>
              <input type="password" required minLength={6} value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} className={inputCls + " font-mono"} data-testid="settings-pwd-new" />
            </Field>
            <button type="submit" disabled={pwdSaving} data-testid="settings-pwd-submit" className="w-full bg-[#111113] hover:bg-black text-white text-xs font-bold uppercase tracking-wider px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              <KeyRound size={14} /> {pwdSaving ? "Menyimpan…" : "Ubah Password Pengaturan"}
            </button>
          </div>
        </form>
      </div>

      {/* Catalog Jasa & Material */}
      <div className="max-w-5xl mt-8">
        <CatalogManager />
      </div>

      {/* Kategori Pengeluaran (dipakai di menu Pengeluaran) */}
      <div className="max-w-5xl mt-8">
        <ExpenseCategoryManager />
      </div>

      {/* Google Drive Connect */}
      <div className="max-w-5xl mt-8" data-testid="drive-card">
        <div className="bg-white border border-[#E4E4E7]">
          <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
            <HardDrive size={14} />
            <div className="label-cap">Google Drive · Dokumentasi Order</div>
          </div>
          <div className="p-6">
            {drive.connected ? (
              <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="drive-connected">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-block w-2 h-2 bg-[#059669] rounded-full" />
                    <span className="text-sm font-bold">Terhubung</span>
                  </div>
                  <div className="text-xs text-[#52525B] font-mono">{drive.email || "(akun Google)"}</div>
                  <div className="text-xs text-[#52525B] mt-2 max-w-md">File yang Anda upload pada Order akan tersimpan di folder "Order.OS Dokumentasi" di Drive Anda. Setiap order otomatis dibuatkan sub-folder dengan nama <code>{"{order_no} - {tanggal} - {nama pelanggan}"}</code>.</div>
                </div>
                <button onClick={disconnectDrive} data-testid="drive-disconnect-button" className="bg-white hover:bg-[#DC2626]/5 text-[#DC2626] border border-[#DC2626]/30 text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
                  <Unplug size={14} /> Putuskan
                </button>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="drive-disconnected">
                <div className="max-w-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-block w-2 h-2 bg-[#52525B] rounded-full" />
                    <span className="text-sm font-bold">Belum Terhubung</span>
                  </div>
                  <div className="text-xs text-[#52525B] mt-1">Hubungkan Google Drive untuk menyimpan dokumentasi file (PDF/Doc/Excel/Foto/Video, maks 100MB per file) di setiap Order. File akan tersimpan di akun Drive Anda — bukan di server kami.</div>
                </div>
                <button onClick={connectDrive} disabled={driveConnecting} data-testid="drive-connect-button" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2 disabled:opacity-60">
                  <LinkIcon size={14} /> {driveConnecting ? "Menghubungkan…" : "Hubungkan Google Drive"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

const inputCls = "w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white";

const Field = ({ label, children, required }) => (
  <div>
    <label className="label-cap block mb-1.5">{label}{required && <span className="text-[#DC2626]">*</span>}</label>
    {children}
  </div>
);

export default Settings;
